import { PreferenceUpgrader, UserPreferenceMap, UserPreferencePath, UserPreferencePathKey, VersionedPreferences } from '../types';
import portfolioPreferencesMigration from './upgrader/portfolioPreferencesMigration';

// One upgrader per preference path. Add a new entry here when a new preference path is introduced.
const upgraders: { [K in UserPreferencePathKey]: PreferenceUpgrader<UserPreferenceMap[K]> } = {
  [UserPreferencePath.ClientsPortfolio]: portfolioPreferencesMigration,
};

// Looks up an upgrader for an arbitrary path string. Returns undefined if the path is unknown.
const findUpgrader = (path: string): PreferenceUpgrader<VersionedPreferences> | undefined => {
  const registry = upgraders as Record<string, PreferenceUpgrader<VersionedPreferences>>;
  return registry[path];
};

// Walks a record from its current version up to `latestVersion`, applying one step per version bump.
// Each step knows how to convert vN into v(N+1) and must bump the version itself.
// Exported so individual upgraders can be unit-tested in isolation without touching the registry.
export const runUpgradeSteps = <T extends VersionedPreferences>(upgrader: PreferenceUpgrader<T>, source: T, label = 'preferences'): T => {
  let current = source;

  while (current.version < upgrader.latestVersion) {
    const step = upgrader.steps[current.version];
    if (!step) {
      throw new Error(`Missing migration step for ${label} at v${current.version}`);
    }

    const next = step(current);
    if (next.version <= current.version) {
      throw new Error(`Migration step for ${label} v${current.version} did not advance the version`);
    }
    current = next;
  }

  if (current.version < upgrader.latestVersion) {
    throw new Error(`Upgrade of ${label} ended at v${current.version}, expected v${upgrader.latestVersion}`);
  }
  return current;
};

// Reads a raw record that came back from the backend and returns it at the latest version.
// Returns null when there is nothing to migrate (e.g. the user has no saved preferences yet).
export const migratePreferences = <T extends VersionedPreferences = VersionedPreferences>(path: string, raw: unknown): T | null => {
  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return null;
  }

  // Records without a `version` field enter as v0 - the blank-slate shape from before any
  // fields existed - and get walked up to `latestVersion` by the registered migration steps.
  const source = raw as Record<string, unknown>;
  const withVersion: VersionedPreferences = {
    ...source,
    version: typeof source.version === 'number' ? source.version : 0,
  };

  const upgrader = findUpgrader(path);
  if (!upgrader) {
    return withVersion as T;
  }
  return runUpgradeSteps(upgrader, withVersion, path) as T;
};

// Stamps a record with the latest version number just before it is sent to the backend.
export const stampLatestPreferencesVersion = <T>(path: string, preferences: T): T => {
  if (preferences === null || preferences === undefined || typeof preferences !== 'object') {
    return preferences;
  }
  const upgrader = findUpgrader(path);
  if (!upgrader) {
    throw new Error(`No preference upgrader registered for path "${path}"`);
  }
  return { ...(preferences as unknown as Record<string, unknown>), version: upgrader.latestVersion } as unknown as T;
};
